<script setup lang="ts">
const props = defineProps({
  craftable: {
    type: Object as PropType<CraftItem>,
    required: true,
  },
});

const emit = defineEmits(["close"]);
const { trackEvent } = useGAnalytics();
const { craft, getIngredientCopies } = useCraftingController();

const isModalCraftLoginShown = ref<boolean>(false);
const isIngredientSelectionShown = ref<boolean>(true);
const isConfirmationShown = ref<boolean>(false);
const isLoadingShown = ref<boolean>(false);
const isResultShown = ref<boolean>(false);
const isResultSuccess = ref<boolean>(false);
const selectedIngredients = ref<Record<string, string[]>>({});
const currentIngredientIndex = ref<number>(0);
const appStateStore = useAppStateStore();

const { data: ingredientCopies, status } = await useAsyncData(
  () => getIngredientCopies(props.craftable.id, appStateStore.currentMerchant),
  {
    default() {
      return [];
    },
  }
);

const onSelectIngredient = (
  ingredientId: string,
  selectedCopyIds: string[]
) => {
  selectedIngredients.value[ingredientId] = selectedCopyIds;
};

const goToNextIngredient = () => {
  if (
    ingredientCopies.value &&
    currentIngredientIndex.value < ingredientCopies.value.length - 1
  ) {
    currentIngredientIndex.value++;
  } else {
    isIngredientSelectionShown.value = false;
    isConfirmationShown.value = true;
  }
};

const goToPreviousIngredient = () => {
  if (currentIngredientIndex.value > 0) {
    currentIngredientIndex.value--;
  }
};

const onBackToIndex = (index: number) => {
  currentIngredientIndex.value = index;
  isIngredientSelectionShown.value = true;
  isConfirmationShown.value = false;
};

const onConfirmCraft = async () => {
  trackEvent("craft_confirm", { id: props.craftable.id });
  isConfirmationShown.value = false;
  isLoadingShown.value = true;

  try {
    const allCopyIds = Object.values(selectedIngredients.value).flat();
    const response = await craft(
      props.craftable.id,
      appStateStore.currentMerchant,
      allCopyIds
    );

    if (response.statusCode === 200) {
      isResultSuccess.value = true;
      trackEvent("craft_success", { id: props.craftable.id });
    }
  } catch (error) {
    trackEvent("craft_error", { id: props.craftable.id });
    isResultSuccess.value = false;
  } finally {
    isLoadingShown.value = false;
    isResultShown.value = true;
  }
};

onMounted(() => {
  if (!useUserStore().isLoggedIn) isModalCraftLoginShown.value = true;
});
</script>

<template>
  <BaseLayoutModal
    @close="$emit('close')"
    width="large"
    :hide-x="isLoadingShown">
    <template #header>
      {{
        isModalCraftLoginShown
          ? $t("main.modalCraftLogin.loginOrSignUp")
          : $t("main.modalCraft.title")
      }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalCraftLogin
          v-if="isModalCraftLoginShown"
          @close="$emit('close')" />
        <ModalCraftIngredient
          v-else-if="
            isIngredientSelectionShown &&
            status === 'success' &&
            ingredientCopies
          "
          :key="currentIngredientIndex"
          :ingredient="ingredientCopies[currentIngredientIndex]"
          :current-step="currentIngredientIndex + 1"
          :total-steps="ingredientCopies.length"
          :selected-ingredients="selectedIngredients"
          @select="onSelectIngredient"
          @next="goToNextIngredient"
          @back="goToPreviousIngredient"
          @close="$emit('close')" />
        <ModalCraftConfirm
          v-else-if="isConfirmationShown"
          :craftable="craftable"
          @confirm="onConfirmCraft"
          @back="
            isIngredientSelectionShown = true;
            isConfirmationShown = false;
          "
          @close="$emit('close')"
          @back-to-index="onBackToIndex" />
        <ModalLoader
          v-else-if="isLoadingShown"
          :title="$t('main.modalLoader.crafting')"
          :body-title="$t('main.modalLoader.craftingInProgress')"
          :body-subtitle="$t('main.modalLoader.craftingUnderway')" />
        <ModalCraftResult
          v-else-if="isResultShown || status === 'error'"
          :craftable="craftable"
          :success="isResultSuccess"
          @close="$emit('close')" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>
<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
